/*
 *  Copyright © Microsoft Corporation. All rights reserved.
 */

/**
 * Splits scenario failures into the ones the service is accountable for and the ones it is not, so that
 * reliability dashboards are not dominated by users cancelling prompts or picking oversized files.
 */
export enum TelemetryErrorType {
    ClientError = 'ClientError',
    ServiceError = 'ServiceError',
}

/**
 * Classifies a failure from an HTTP status code. A 4xx means the request itself was rejected - an
 * unsupported file type, an oversized document, an expired provider token - so it is charged to the
 * client. Everything else, including a missing status, is charged to the service.
 */
export const getTelemetryErrorTypeFromStatus = (status?: number): TelemetryErrorType =>
    typeof status === 'number' && status >= 400 && status < 500
        ? TelemetryErrorType.ClientError
        : TelemetryErrorType.ServiceError;

/**
 * Classifies an arbitrary caught value. Failures raised before a request ever leaves the client - upload
 * prechecks, cancelled auth popups, popup blockers - are thrown as plain strings rather than as HTTP
 * errors, so they are always client errors. Anything else falls back to its HTTP status.
 */
export const getTelemetryErrorType = (error: unknown): TelemetryErrorType =>
    typeof error === 'string' || error instanceof String
        ? TelemetryErrorType.ClientError
        : getTelemetryErrorTypeFromStatus((error as { status?: number })?.status);
